import React, { useRef, useState, useEffect } from "react";
import { trackEvent } from '../utils/analytics';
import { Button } from "semantic-ui-react";
import { motion } from "framer-motion";
import { sendNotification } from '../utils/notify';
import ReCAPTCHA from "react-google-recaptcha";
import "../styles/ContactForm.scss";
import EmailSuggestion, { useEmailSuggestion } from './EmailSuggestion';
import { formatPhone } from '../utils/formatPhone';

const formContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } },
};
const fieldItem = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

function ContactForm({ animateIn = true }) {
  const options = [
    { key: 0, text: "Select One", value: "select" },
    { key: 1, text: "Buying a Home", value: "Buying a Home" },
    { key: 2, text: "Selling a Home", value: "Selling a Home" },
    { key: 3, text: "General Questions", value: "General Questions" },
  ];

  const [emailSent, setEmailSent] = useState(false);
  const [email, setEmail] = useState('');
  const emailHint = useEmailSuggestion(email);
  const [phone, setPhone] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [captchaValue, setCaptchaValue] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(options[0]);
  const form = useRef();
  const captchaRef = useRef();
  const dropdownRef = useRef();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCaptchaExpired = () => {
    setCaptchaValue(null);
    captchaRef.current?.reset();
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    if (!captchaValue) {
      alert("Please verify you are not a robot.");
      return;
    }

    setEmailSending(true);
    setSendError('');

    const fd = new FormData(form.current);
    try {
      await sendNotification({
        type: 'contact',
        first_name: fd.get('first_name'),
        last_name: fd.get('last_name'),
        email: fd.get('email'),
        phone_number: fd.get('phone_number'),
        buysell: fd.get('buysell'),
        message: fd.get('message'),
      });
      setEmailSending(false);
      setEmailSent(true);
      trackEvent('contact_form_submit');
    } catch (err) {
      // Without a visible error the form just reverted to "Submit", so a failed send
      // looked identical to not having pressed the button — the message was lost and
      // neither the visitor nor Lily knew.
      console.error('Contact form send failed:', err);
      setEmailSending(false);
      setSendError("Something went wrong, please try again, or email lily@lilylaisf.com directly.");
      trackEvent('contact_form_error');
    }
  };

  return (
    <div className="contact-form">
      <motion.form
        ref={form}
        onSubmit={sendEmail}
        className="form-fields"
        variants={formContainer}
        initial={animateIn ? 'hidden' : false}
        animate="visible"
      >
        <motion.div className="form-field" variants={fieldItem}>
          <label htmlFor="first_name">First Name</label>
          <input id="first_name" type="text" name="first_name" required placeholder="Jane" autoComplete="given-name" />
        </motion.div>

        <motion.div className="form-field" variants={fieldItem}>
          <label htmlFor="last_name">Last Name</label>
          <input id="last_name" type="text" name="last_name" required placeholder="Doe" autoComplete="family-name" />
        </motion.div>

        <motion.div className="form-field" variants={fieldItem}>
          <label htmlFor="email">Email Address</label>
          <input id="email" type="email" name="email" required placeholder="jane@email.com" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={emailHint.onBlur} />
          <EmailSuggestion suggestion={emailHint.suggestion} onAccept={setEmail} />
        </motion.div>

        <motion.div className="form-field" variants={fieldItem}>
          <label htmlFor="phone_number">Phone Number</label>
          <input id="phone_number" type="tel" name="phone_number" required placeholder="(___) ___-____" autoComplete="tel" inputMode="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} />
        </motion.div>

        <motion.div className="form-field" variants={fieldItem}>
          <label htmlFor="buysell-trigger">Are you interested in buying or selling?</label>
          <div
            id="buysell-trigger"
            className={`custom-select ${dropdownOpen ? 'open' : ''}`}
            ref={dropdownRef}
            role="combobox"
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            aria-controls="buysell-options"
            tabIndex={0}
            onClick={() => setDropdownOpen(!dropdownOpen)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDropdownOpen(!dropdownOpen); }
              if (e.key === 'Escape') setDropdownOpen(false);
            }}
          >
            <span className={selectedOption.key === 0 ? 'custom-select-placeholder' : ''}>
              {selectedOption.text}
            </span>
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" aria-hidden="true">
              <path d="M1 1l5 5 5-5" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            {dropdownOpen && (
              <div id="buysell-options" className="custom-select-options" role="listbox">
                {options.slice(1).map((o) => (
                  <div
                    key={o.key}
                    className={`custom-select-option ${selectedOption.key === o.key ? 'selected' : ''}`}
                    role="option"
                    aria-selected={selectedOption.key === o.key}
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); setSelectedOption(o); setDropdownOpen(false); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); setSelectedOption(o); setDropdownOpen(false); }
                    }}
                  >
                    {o.text}
                  </div>
                ))}
              </div>
            )}
            <input type="hidden" name="buysell" value={selectedOption.value} />
          </div>
        </motion.div>

        <motion.div className="form-field" variants={fieldItem}>
          <label htmlFor="message">How did you hear about me?</label>
          <textarea id="message" name="message" required placeholder="I found you through a friend and I'm looking to buy a home in San Francisco..." />
        </motion.div>

        <motion.div className="form-field" variants={fieldItem}>
          <ReCAPTCHA
            ref={captchaRef}
            sitekey="6Ldx-mUsAAAAAF9gnrvdButSomCO_hFeC19NzFmw"
            onChange={setCaptchaValue}
            onExpired={handleCaptchaExpired}
            onErrored={handleCaptchaExpired}
            className="captcha"
          />
        </motion.div>

        <motion.div className="form-field" variants={fieldItem}>
          {!emailSent && !emailSending && (
            <Button type="submit" basic disabled={!captchaValue}>Submit</Button>
          )}
          {!emailSent && emailSending && <Button loading basic />}
          {emailSent && !emailSending && (
            <Button disabled basic>Email Sent</Button>
          )}
          {sendError && <p className="form-error" role="alert">{sendError}</p>}
        </motion.div>
      </motion.form>
    </div>
  );
}

export default ContactForm;
